import { isCommonNode, NodeType } from '@vedalib/editor'
import React, { useMemo } from 'react'

import { getNode } from 'components/Editor/EditorContext/selectors/block'
import ErrorBoundary from 'components/Editor/EditorElements/decorators/ErrorBoundary'
import NodeV2 from 'components/Editor/NodeV2'
import { EMPTY_ARRAY } from 'constants/commonConstans'
import { t } from 'services/Translation'

import Absolute from '../Absolute'
import Column from '../Column'
import Row from '../Row'
import Shell from '../Shell'
import * as s from './Node.module.scss'
import type { INodeComponentProps, INodeProps } from './types'

const BLOCK_COMPONENTS: Record<NodeType, React.FC<INodeComponentProps>> = {
  [NodeType.absolute]: Absolute as React.FC<INodeComponentProps>,
  [NodeType.shell]: Shell as React.FC<INodeComponentProps>,
  [NodeType.row]: Row as React.FC<INodeComponentProps>,
  [NodeType.column]: Column as React.FC<INodeComponentProps>,
  [NodeType.node]: NodeV2 as React.FC<INodeComponentProps>,
}

const NODE_ORDER = [NodeType.absolute, NodeType.column, NodeType.row, NodeType.shell, NodeType.node]

const Node: React.FC<INodeProps> = ({
  id,
  block,
  mode,
  isItem = false,
  isSlot = false,
  isPreview = false,
  parent = null,
  level = 0,
}) => {
  const node = getNode(block, id)
  const Component = node && BLOCK_COMPONENTS[node.type]

  const childNodes = useMemo(() => {
    const childNodes = node?.children?.map((id) => getNode(block, id)) || EMPTY_ARRAY
    childNodes?.sort((a, b) => {
      const aI = NODE_ORDER.indexOf(a.type)
      const bI = NODE_ORDER.indexOf(b.type)
      return aI - bI
    })
    return childNodes
  }, [block, node])

  if (!Component) {
    console.warn(`Component for node.id:${id} | node:${JSON.stringify(node, null, 2)} not found`)
    return null
  }

  return (
    <ErrorBoundary errorChildren={<div className={s.error}>{t('editor.systemBlockError')}</div>}>
      <Component
        block={block}
        isItem={isItem}
        isPreview={isPreview}
        isSlot={isSlot}
        level={level}
        mode={mode}
        node={node}
        parent={parent}
      >
        {isCommonNode(node) &&
          childNodes.map((child) => (
            <Node
              block={block}
              id={child.id}
              isItem={isItem}
              key={child.id}
              level={level + 1}
              mode={mode}
              parent={node}
            />
          ))}
      </Component>
    </ErrorBoundary>
  )
}

export default React.memo(Node)
